import _ from 'underscore';
import React, { Component } from 'react';
import onClickOutside from 'react-onclickoutside';
import { EntitySelector } from '@bingads-webui-react/entity-selector';
import { FocusLock } from '@bingads-webui-react/focus-lock';
import { curlyTemplate } from '@bingads-webui-universal/primitive-utilities';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ListSelector } from '@bingads-webui-react/list-selector';
import { fixSVGClassListSupportForIE } from './shim';

// Start your code from here for component react-example

class DropdownSelector extends Component {
  static defaultProps = {
    pageSize: 10,
    customItems: [],
    onFetch: null,
    isDisabled: false,
    onToggle: () => {},
    mode: 'simple',
    renderItem: item => (
      <li className="list-selector-option" role="presentation" >
        <span className={item.status ? item.status : ''}>{item.value}</span>
      </li>
    ),
    renderSelectedItem: item => <span className="truncated">{item.value}</span>,
    renderEmptyList: undefined,
    enableRandomPage: false,
    iconClass: 'glyphicon glyphicon-triangle-bottom',
    enableArrowKeysSupport: false,
    customMenuAriaLabel: '',
    dropdownMenuAriaLabel: '',
    getDropdownButtonA11yLabel: (selectedItem, i18n) => {
      const templateString = i18n.getString(_TL_('{{itemName}} Selected'));

      return !templateString ? undefined : curlyTemplate(templateString)({
        itemName: selectedItem.value,
      });
    },
    shouldFocusFirstFocusableItemOnMount: false,
    className: undefined,
  };

  state = {
    isToggled: false,
  };

  componentDidMount() {
    fixSVGClassListSupportForIE();
  }

  onSelect = (selectedItem) => {
    this.toggle();
    this.props.onSelect(selectedItem);
  };

  handleClickOutside = (e) => {
    // handle click outside to dismiss menu
    if (this.state.isToggled) {
      this.toggle();
    }
    e.stopPropagation();
  };

  toggle = () => {
    const isToggled = !this.state.isToggled;
    if (_.isFunction(this.props.onToggle)) {
      this.props.onToggle();
    }
    this.setState({ isToggled });
  };

  render() {
    const {
      items,
      selectedItem,
      customItems,
      renderItem,
      iconClass,
      enableArrowKeysSupport,
      i18n,
      customMenuAriaLabel,
      dropdownMenuAriaLabel,
    } = this.props;
    let dropDownMenu;

    if (this.props.mode === 'async') {
      dropDownMenu = (
        <EntitySelector
          pageSize={this.props.pageSize}
          onFetch={this.props.onFetch}
          items={items}
          selectedItem={_.findWhere(items, { id: selectedItem.id })}
          totalCount={this.props.totalCount}
          onSelect={this.onSelect}
          renderItem={renderItem}
          renderEmptyList={this.props.renderEmptyList}
          enableRandomPage={this.props.enableRandomPage}
          i18n={i18n}
          enableArrowKeysSupport={enableArrowKeysSupport}
          ariaLabel={dropdownMenuAriaLabel}
        />
      );
    } else {
      dropDownMenu = (
        <div className="list-selector-container">
          <ListSelector
            renderItem={renderItem}
            renderEmptyList={this.props.renderEmptyList}
            onSelect={this.onSelect}
            data={items}
            selectedItem={_.findWhere(items, { id: selectedItem.id })}
            enableArrowKeysSupport={enableArrowKeysSupport}
            ariaLabel={dropdownMenuAriaLabel}
          />
        </div>
      );
    }

    return (
      <div
        className={classnames(
          'ba-dropdown-selector',
          this.state.isToggled ? 'open' : ''
        )}
      >
        <button
          className={classnames(
            'btn btn-default dropdown-toggle',
            this.props.className
          )}
          type="button"
          disabled={this.props.isDisabled}
          tabIndex="0"
          onClick={this.toggle}
          style={{ display: 'flex', justifyContent: 'space-between' }}
          aria-haspopup
          aria-expanded={this.state.isToggled}
          aria-label={this.props.getDropdownButtonA11yLabel(selectedItem, i18n)}
        >
          {this.props.renderSelectedItem(selectedItem)}
          <div className={classnames('icon-right', iconClass)} />
        </button>
        <div className={classnames('dropdown-menu', 'react-dropdown-menu')} >
          <FocusLock
            onExit={this.handleClickOutside}
            focusFirstItemOnMount={this.props.shouldFocusFirstFocusableItemOnMount}
            isControlledFocusLock
            isFocusLock={this.state.isToggled}
          >
            {customItems.length > 0 && (
              <div className="custom-selector-container">
                <ListSelector
                  renderItem={renderItem}
                  onSelect={this.onSelect}
                  data={customItems}
                  selectedItem={_.findWhere(customItems, { id: selectedItem.id })}
                  enableArrowKeysSupport={enableArrowKeysSupport}
                  ariaLabel={customMenuAriaLabel}
                />
              </div>
            )}
            {dropDownMenu}
          </FocusLock>
        </div>
      </div>
    );
  }
}

DropdownSelector.propTypes = {
  /**
   * dropdown disabled
   */
  isDisabled: PropTypes.bool,
  /**
   * item count per page
   */
  pageSize: PropTypes.number,
  /**
   * onFetch sync function to update the dropdown list
   * @param {object} - contain query(search term) and pageNumber info
   * @return {Promise} - contains total count and items for the result
   */
  onFetch: PropTypes.func,
  /**
   * Selected item in the list
   */
  selectedItem: PropTypes.shape({
    id: PropTypes.any,
    value: PropTypes.any,
  }).isRequired,
  /**
   * Gets called when user select an dropdown item.
   * @param {string} - input query
   */
  onSelect: PropTypes.func.isRequired,
  /**
   * Gets called when user toggle filter.
   */
  onToggle: PropTypes.func,
  /**
   * user customed dropdown item.
   */
  customItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.any,
    value: PropTypes.any,
  })),
  /**
   * A array of data.
   */
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  /**
   * total item count, which might not equal
   */
  totalCount: PropTypes.number.isRequired,
  /**
   * Mode, currently we have async and simple mode
   * when async mode is selected., onFetch function is required
   */
  mode: PropTypes.oneOf(['simple', 'async']),
  /**
   * i18n for localization
   */
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    parseDecimal: PropTypes.func.isRequired,
  }).isRequired,
  /**
   * custom render function for each item
   */
  renderItem: PropTypes.func,
  /**
   * custom render function for selected item
   */
  renderSelectedItem: PropTypes.func,
  /**
   * Rendered when the list is empty. A render function that returns a React element.
   */
  renderEmptyList: PropTypes.func,
  /**
   * enable random page
   */
  enableRandomPage: PropTypes.bool,
  /**
   * custom dropdown icon
   */
  iconClass: PropTypes.string,
  /**
   * Used to check if arrow keys are supported to manage focus state.
   *
   */
  enableArrowKeysSupport: PropTypes.bool,
  /**
   * Used to specify aria label for the custom menu.
   *
   */
  customMenuAriaLabel: PropTypes.string,
  /**
   * Used to specify aria label for the dropdown menu.
   *
   */
  dropdownMenuAriaLabel: PropTypes.string,
  /**
   * Used to specify aria label for the dropdown button
   */
  getDropdownButtonA11yLabel: PropTypes.func,
  /**
   * Used to indicate if focus to the first focusable item of selector
   */
  shouldFocusFirstFocusableItemOnMount: PropTypes.bool,
  /**
   * Used to add more classname to the dropdown-toggle button
   */
  className: PropTypes.string,
};

export const EnhancedDropdownSelector = onClickOutside(DropdownSelector);
